import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';

import {Filtergroup} from '../../types';
import {configKeys} from '../questionnaire-editor.config-keys';
import {AuthUriService} from './auth-uri.service';

import {ConfigService} from '@ngmedax/config';
import {Questionnaire} from '@ngmedax/common-questionnaire-types';


@Injectable()
export class ApiService {

  public constructor(
    private http: HttpClient,
    private configService: ConfigService,
    private authUriService: AuthUriService) {
  }

  /**
   * Returns true if questionnaire exists
   *
   * @param {string} questionnaireId
   */
  public async hasQuestionnaire(questionnaireId: string) {
    try {
      const questionnaire = await this.loadQuestionnaire(questionnaireId);
      return !!questionnaire.questionnaire;
    } catch (error) {
      return false;
    }
  }

  /**
   * Loads questionnaire by given id
   *
   * @param {string} questionnaireId
   * @returns {Promise<{$schema: string, questionnaire: Questionnaire}>}
   */
  public loadQuestionnaire(questionnaireId: string): Promise<{$schema: string, questionnaire: Questionnaire}> {
    const basePath = this.authUriService.buildUrl(configKeys.QUESTIONNAIRE_URI_CONFIG_KEY, `/${questionnaireId}`);
    const headers = this.authUriService.getAuthHeaders();

    return new Promise((resolve, reject) => {
      this.http.get(basePath, {headers})
        .subscribe(
          (questionnaire: {$schema: string; questionnaire: any}) => {

            if (!questionnaire.questionnaire) {
              reject(new Error('Invalid questionnaire format. Missing property: "questionnaire"'));
            }

            resolve(questionnaire);
          },
          error => {
            reject(error);
          });
    });
  }

  /**
   * Saves a questionnaire
   *
   * @returns {Promise<any>}
   */
  public createQuestionnaire(payload: {$schema: string, questionnaire: Questionnaire}): Promise<any> {
    payload.questionnaire.updatedAt = new Date().toJSON();

    if (!payload.questionnaire.createdAt) {
      payload.questionnaire.createdAt = new Date().toJSON();
    }

    const basePath = this.authUriService.buildUrl(configKeys.QUESTIONNAIRE_URI_CONFIG_KEY);
    const headers = this.authUriService.getAuthHeaders();

    return new Promise<void>((resolve, reject) => {
      this.http.post(basePath, payload, {headers})
        .subscribe(
          () => resolve(),
          error => reject(error));
    });
  }

  /**
   * Updates a questionnaire
   *
   * @returns {Promise<any>}
   */
  public updateQuestionnaire(payload: {$schema: string, questionnaire: Questionnaire}): Promise<any> {
    const uid = payload.questionnaire.id;

    payload.questionnaire.updatedAt = new Date().toJSON();

    if (!payload.questionnaire.createdAt) {
      payload.questionnaire.createdAt = new Date().toJSON();
    }

    const basePath = this.authUriService.buildUrl(configKeys.QUESTIONNAIRE_URI_CONFIG_KEY, `/${uid}`);
    const headers = this.authUriService.getAuthHeaders();

    return new Promise<void>((resolve, reject) => {
      this.http.put(basePath, payload, {headers})
        .subscribe(
          () => resolve(),
          error => reject(error));
    });
  }

  /**
   * Fetches number of questionnaires
   *
   * @returns {Promise<number>}
   */
  public async getNumQuestionnaires(filterByUser: boolean = false): Promise<number> {
    const basePath = this.authUriService.buildUrl(configKeys.QUESTIONNAIRE_URI_CONFIG_KEY, '/count')
      + (filterByUser? '?filterByUser=true' : '');
    const headers = this.authUriService.getAuthHeaders();

    return new Promise<number>((resolve, reject) => {
      this.http.get(basePath, {headers}).subscribe(
        (response: {count: number}) => resolve(response.count || 0),
        error => reject(error)
      );
    });
  }

  /**
   * Returns filter groups
   *
   * @returns {Promise<Filtergroup[]>}
   */
  public loadFiltergroups(): Promise<Filtergroup[]> {
    const basePath = this.authUriService.buildUrl(configKeys.FILTER_GROUP_URI_CONFIG_KEY, '/?filterByUser=true');
    const headers = this.authUriService.getAuthHeaders();

    return new Promise((resolve, reject) => {
      this.http.get(basePath, {headers})
        .subscribe((filtergroups: any) => {
            resolve(filtergroups.filterGroup || filtergroups.filtergroup || filtergroups || []);
          },
          error => {
            reject(error);
          });
    });
  }

  /**
   * Returns whether the ai assistant feature is available on the backend
   *
   * @returns {Promise<any>}
   */
  public getAiStatus(): Promise<any> {
    const basePath = this.authUriService.buildUrl(configKeys.QUESTIONNAIRE_URI_CONFIG_KEY, '/ai/status');
    const headers = this.authUriService.getAuthHeaders();

    return new Promise((resolve, reject) => {
      this.http.get(basePath, {headers})
        .subscribe((response: any) => resolve(response), error => reject(error));
    });
  }

  /**
   * Creates a new ai assistant session
   *
   * @param {string} prompt Initial user prompt
   * @param {any} context Current questionnaire payload or null
   * @param {string} locale Locale of the user interface
   * @param {any[]} files Optional file attachments: {name, mime, data (base64)}
   * @returns {Promise<any>}
   */
  public createAiSession(prompt: string, context: any, locale: string, files?: any[]): Promise<any> {
    const basePath = this.authUriService.buildUrl(configKeys.QUESTIONNAIRE_URI_CONFIG_KEY, '/ai/session');
    const headers = this.authUriService.getAuthHeaders();
    const body: any = {prompt, context, locale};

    if (files && files.length) {
      body.files = files;
    }

    return new Promise((resolve, reject) => {
      this.http.post(basePath, body, {headers})
        .subscribe((response: any) => resolve(response), error => reject(error));
    });
  }

  /**
   * Returns the state of an ai assistant session
   *
   * @param {string} sessionId
   * @param {number} after Only return messages after this sequence number
   * @param {number} resultVersion Result version already known to the client
   * @returns {Promise<any>}
   */
  public getAiSession(sessionId: string, after: number, resultVersion: number): Promise<any> {
    const basePath = this.authUriService
      .buildUrl(configKeys.QUESTIONNAIRE_URI_CONFIG_KEY, `/ai/session/${sessionId}?after=${after}&rv=${resultVersion}`);
    const headers = this.authUriService.getAuthHeaders();

    return new Promise((resolve, reject) => {
      this.http.get(basePath, {headers})
        .subscribe((response: any) => resolve(response), error => reject(error));
    });
  }

  /**
   * Sends a user message to an ai assistant session
   *
   * @param {string} sessionId
   * @param {string} text
   * @param {any[]} files Optional file attachments: {name, mime, data (base64)}
   * @returns {Promise<any>}
   */
  public sendAiSessionMessage(sessionId: string, text: string, files?: any[]): Promise<any> {
    const basePath = this.authUriService.buildUrl(configKeys.QUESTIONNAIRE_URI_CONFIG_KEY, `/ai/session/${sessionId}/message`);
    const headers = this.authUriService.getAuthHeaders();
    const body: any = {text};

    if (files && files.length) {
      body.files = files;
    }

    return new Promise((resolve, reject) => {
      this.http.post(basePath, body, {headers})
        .subscribe((response: any) => resolve(response), error => reject(error));
    });
  }

  /**
   * Deletes an ai assistant session
   *
   * @param {string} sessionId
   * @returns {Promise<any>}
   */
  public deleteAiSession(sessionId: string): Promise<any> {
    const basePath = this.authUriService.buildUrl(configKeys.QUESTIONNAIRE_URI_CONFIG_KEY, `/ai/session/${sessionId}`);
    const headers = this.authUriService.getAuthHeaders();

    return new Promise((resolve, reject) => {
      this.http.delete(basePath, {headers})
        .subscribe((response: any) => resolve(response), error => reject(error));
    });
  }
}
